/**
 * Credit Notes Page
 * Manage credit notes and refunds
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { useClientStore } from '../store/clientStore';
import { useInvoiceStore } from '../store/invoiceStore';
import { useDataSubscriptions } from '../hooks/useDataSubscriptions';
import { formatCurrency, formatDate } from '../utils/formatters';
import {
  validateCreditNoteAmount,
  getAvailableCredit,
} from '../services/creditNoteService';
import {
  getCreditNotes,
  createCreditNote,
  issueCreditNote,
  voidCreditNote,
  applyCreditToInvoice,
} from '../services/creditNoteFirestore';
import './CreditNotes.css';

const CreditNotes = () => {
  useDataSubscriptions();

  const user = useAuthStore((state) => state.user);
  const clients = useClientStore((state) => state.clients);
  const invoices = useInvoiceStore((state) => state.invoices);

  const [creditNotes, setCreditNotes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    clientId: '',
    invoiceId: '',
    amount: '',
    reason: '',
    currency: 'USD',
  });
  const [formErrors, setFormErrors] = useState({});

  // Load credit notes from Firestore
  const loadCreditNotes = useCallback(async () => {
    if (!user?.uid) return;

    try {
      setPageLoading(true);
      const notes = await getCreditNotes(user.uid);
      setCreditNotes(notes);
    } catch (error) {
      console.error('Error loading credit notes:', error);
    } finally {
      setPageLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    loadCreditNotes();
  }, [loadCreditNotes]);

  const filteredNotes = creditNotes.filter((note) => {
    if (filter === 'draft') return note.status === 'draft';
    if (filter === 'issued') return note.status === 'issued';
    if (filter === 'applied') return note.status === 'applied';
    return true;
  });

  const getStatusBadge = (status) => {
    const badges = {
      draft: <span className="badge badge-info">Draft</span>,
      issued: <span className="badge badge-success">Issued</span>,
      applied: <span className="badge badge-warning">Applied</span>,
      refunded: <span className="badge badge-primary">Refunded</span>,
      void: <span className="badge badge-danger">Void</span>,
    };
    return badges[status] || <span className="badge">{status}</span>;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: null }));
  };

  const selectedClient = clients.find((c) => c.id === formData.clientId);
  const clientInvoices = invoices.filter(
    (inv) => inv.clientId === formData.clientId && inv.status !== 'paid'
  );
  const selectedInvoice = invoices.find((inv) => inv.id === formData.invoiceId);

  const validateForm = () => {
    const errors = {};

    if (!formData.clientId) {
      errors.clientId = 'Please select a client';
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      errors.amount = 'Please enter a valid amount';
    }

    if (selectedInvoice) {
      const validation = validateCreditNoteAmount(
        parseFloat(formData.amount),
        selectedInvoice
      );
      if (!validation.valid) {
        errors.amount = validation.error;
      }
    }

    if (!formData.reason.trim()) {
      errors.reason = 'Please enter a reason';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await createCreditNote(user.uid, {
        clientId: formData.clientId,
        clientName: selectedClient?.name || '',
        invoiceId: formData.invoiceId || null,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        reason: formData.reason,
      });

      // Reload credit notes
      await loadCreditNotes();

      setShowModal(false);
      setFormData({
        clientId: '',
        invoiceId: '',
        amount: '',
        reason: '',
        currency: 'USD',
      });

      alert('Credit note created successfully!');
    } catch (error) {
      alert('Error creating credit note: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleIssue = async (creditNote) => {
    if (!window.confirm('Issue this credit note? This will make it available for use.')) {
      return;
    }

    try {
      await issueCreditNote(creditNote.id);
      await loadCreditNotes();
    } catch (error) {
      alert('Error issuing credit note: ' + error.message);
    }
  };

  const handleVoid = async (creditNote) => {
    if (!window.confirm('Void this credit note? This cannot be undone.')) {
      return;
    }

    try {
      await voidCreditNote(creditNote.id);
      await loadCreditNotes();
    } catch (error) {
      alert('Error voiding credit note: ' + error.message);
    }
  };

  const handleApply = async (creditNote) => {
    // For now, show a simple prompt for invoice selection
    const invoiceId = window.prompt('Enter Invoice ID to apply credit to:');
    if (!invoiceId) return;

    const amount = window.prompt(
      `Enter amount to apply (max ${formatCurrency(getAvailableCredit(creditNote))}):`
    );
    if (!amount) return;

    try {
      await applyCreditToInvoice(creditNote.id, invoiceId, parseFloat(amount));
      await loadCreditNotes();
      alert('Credit applied successfully!');
    } catch (error) {
      alert('Error applying credit: ' + error.message);
    }
  };

  // Calculate totals
  const totals = {
    total: creditNotes.reduce((sum, cn) => sum + cn.amount, 0),
    issued: creditNotes
      .filter((cn) => cn.status === 'issued')
      .reduce((sum, cn) => sum + getAvailableCredit(cn), 0),
    applied: creditNotes
      .filter((cn) => cn.status === 'applied' || cn.status === 'issued')
      .reduce((sum, cn) => {
        const applied = (cn.appliedTo || []).reduce((s, a) => s + a.amount, 0);
        return sum + applied;
      }, 0),
  };

  if (pageLoading) {
    return (
      <div className="credit-notes-page">
        <div className="page-header">
          <div>
            <h1>Credit Notes</h1>
            <p className="page-subtitle">Manage credits and refunds for your clients</p>
          </div>
        </div>
        <div className="loading-state">Loading credit notes...</div>
      </div>
    );
  }

  return (
    <div className="credit-notes-page">
      <div className="page-header">
        <div>
          <h1>Credit Notes</h1>
          <p className="page-subtitle">Manage credits and refunds for your clients</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          + New Credit Note
        </button>
      </div>

      {/* Summary Cards */}
      <div className="credit-summary-cards">
        <div className="summary-card">
          <div className="summary-icon">💳</div>
          <div className="summary-content">
            <div className="summary-label">Total Issued</div>
            <div className="summary-value">{formatCurrency(totals.total)}</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">✅</div>
          <div className="summary-content">
            <div className="summary-label">Available Credit</div>
            <div className="summary-value">{formatCurrency(totals.issued)}</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">📝</div>
          <div className="summary-content">
            <div className="summary-label">Applied</div>
            <div className="summary-value">{formatCurrency(totals.applied)}</div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({creditNotes.length})
        </button>
        <button
          className={`filter-btn ${filter === 'draft' ? 'active' : ''}`}
          onClick={() => setFilter('draft')}
        >
          Draft
        </button>
        <button
          className={`filter-btn ${filter === 'issued' ? 'active' : ''}`}
          onClick={() => setFilter('issued')}
        >
          Issued
        </button>
        <button
          className={`filter-btn ${filter === 'applied' ? 'active' : ''}`}
          onClick={() => setFilter('applied')}
        >
          Applied
        </button>
      </div>

      {/* Credit Notes List */}
      {filteredNotes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📄</div>
          <h3>No credit notes yet</h3>
          <p>Create a credit note to issue refunds or credits to your clients.</p>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            Create Credit Note
          </button>
        </div>
      ) : (
        <div className="credit-notes-list">
          <table className="data-table">
            <thead>
              <tr>
                <th>Credit Note #</th>
                <th>Client</th>
                <th>Amount</th>
                <th>Available</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredNotes.map((note) => (
                <tr key={note.id}>
                  <td className="font-mono">{note.creditNoteNumber}</td>
                  <td>{note.clientName}</td>
                  <td className="font-bold">{formatCurrency(note.amount)}</td>
                  <td>{formatCurrency(getAvailableCredit(note))}</td>
                  <td className="reason-cell">{note.reason}</td>
                  <td>{getStatusBadge(note.status)}</td>
                  <td>{formatDate(note.createdAt)}</td>
                  <td>
                    <div className="action-buttons">
                      {note.status === 'draft' && (
                        <>
                          <button
                            className="btn-sm btn-primary"
                            onClick={() => handleIssue(note)}
                            title="Issue credit note"
                          >
                            Issue
                          </button>
                          <button
                            className="btn-sm btn-danger"
                            onClick={() => handleVoid(note)}
                            title="Void"
                          >
                            Void
                          </button>
                        </>
                      )}
                      {note.status === 'issued' && (
                        <button
                          className="btn-sm btn-secondary"
                          onClick={() => handleApply(note)}
                          title="Apply to invoice"
                        >
                          Apply
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Credit Note Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>New Credit Note</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Client *</label>
                  <select
                    className={`input ${formErrors.clientId ? 'input-error' : ''}`}
                    value={formData.clientId}
                    onChange={(e) => handleInputChange('clientId', e.target.value)}
                  >
                    <option value="">Select a client</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.clientId && (
                    <span className="error-text">{formErrors.clientId}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Related Invoice (optional)</label>
                  <select
                    className="input"
                    value={formData.invoiceId}
                    onChange={(e) => handleInputChange('invoiceId', e.target.value)}
                    disabled={!formData.clientId}
                  >
                    <option value="">No linked invoice</option>
                    {clientInvoices.map((invoice) => (
                      <option key={invoice.id} value={invoice.id}>
                        {invoice.invoiceNumber} - {formatCurrency(invoice.totalAmount)}
                      </option>
                    ))}
                  </select>
                  <span className="help-text">
                    Link to an invoice for corrections or partial refunds
                  </span>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Amount *</label>
                    <input
                      type="number"
                      className={`input ${formErrors.amount ? 'input-error' : ''}`}
                      value={formData.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                    {formErrors.amount && (
                      <span className="error-text">{formErrors.amount}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Currency</label>
                    <select
                      className="input"
                      value={formData.currency}
                      onChange={(e) => handleInputChange('currency', e.target.value)}
                    >
                      <option value="USD">USD</option>
                      <option value="PHP">PHP</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Reason *</label>
                  <textarea
                    className={`input ${formErrors.reason ? 'input-error' : ''}`}
                    value={formData.reason}
                    onChange={(e) => handleInputChange('reason', e.target.value)}
                    placeholder="Describe the reason for this credit..."
                    rows="3"
                  />
                  {formErrors.reason && (
                    <span className="error-text">{formErrors.reason}</span>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create Credit Note'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditNotes;
